import { isString } from 'es-toolkit';
import { utils } from 'xlsx';

import type { Nullable, SimpleAnyObject } from '@clearverify/types';

import type {
  DroppedFilesValidationFunction,
  DroppedFileValidationResult,
} from 'src/components/finalForm';
import { readExcelFile, trimExcelFileHeadings } from 'src/utils/excel';
import { validateHeadings } from 'src/utils/validateHeadings';

export const createFilesValidation = (templateSchema: SimpleAnyObject) => {
  const validationFunction: DroppedFilesValidationFunction = async droppedFiles => {
    const files: DroppedFileValidationResult[] = await Promise.all(
      droppedFiles.map(async fileInfo => {
        const excelWorkbook = await readExcelFile(fileInfo.file);
        if (!excelWorkbook) return { fileId: fileInfo.id, valid: false };

        const sheets = excelWorkbook.Sheets;
        for (const sheetsName of excelWorkbook.SheetNames) {
          if (!sheetsName) return { fileId: fileInfo.id, valid: false, message: 'Sheet not found' };

          const sheet = sheets[sheetsName];
          if (!sheet) return { fileId: fileInfo.id, valid: false, message: 'Sheet not found' };

          const excelWorkbookData = utils.sheet_to_json<Nullable<string>[]>(sheet, {
            header: 1,
            blankrows: false,
          });

          // we also count the headings row
          if (excelWorkbookData.length < 2) {
            return { fileId: fileInfo.id, valid: false, message: 'The file is empty' };
          }

          const headings = trimExcelFileHeadings(excelWorkbookData[0]);
          if (!headings) {
            continue;
          }

          const definedSchema = formatValuesInSchema(templateSchema, false);

          const valid = validateHeadings(headings, Object.values(definedSchema));

          if (valid) {
            return { fileId: fileInfo.id, valid: true };
          }
        }

        return { fileId: fileInfo.id, valid: false, message: 'Missing required headings' };
      }),
    );

    const output: DroppedFileValidationResult[] = droppedFiles.map(fileInfo => {
      const validatedFile = files.find(
        validatedFileInfo => validatedFileInfo.fileId === fileInfo.id,
      );

      return {
        fileId: fileInfo.id,
        valid: !!validatedFile?.valid,
        message: validatedFile?.message,
      };
    });

    return output;
  };

  return validationFunction;
};

const formatValuesInSchema = <T extends SimpleAnyObject>(object: T, lowerCase = true): T => {
  const entries = Object.entries(object);
  const formattedEntries = entries.map(item => {
    const [key, value] = item;
    if (lowerCase) {
      return [key, isString(value) ? value.trim().toLocaleLowerCase() : value];
    }
    return [key, isString(value) ? value.trim() : value];
  });

  return Object.fromEntries(formattedEntries);
};
